import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";

const inputCls =
  "mt-1.5 w-full bg-[#122A3F] border border-white/10 rounded-sm px-4 py-3 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B] focus:border-[#EA7B2B] transition-colors";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");

  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setDone(true);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("La password deve avere almeno 8 caratteri."); return; }
    if (pw !== pw2) { setError("Le due password non coincidono."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A1826] px-8 py-12" data-testid="reset-password-page">
      <div className="w-full max-w-sm">
        <div className="mb-10">
          <img src="/rebreton-logo-bianco.webp" alt="Rebreton Real Estate" className="h-11 w-auto object-contain object-left" />
          <div className="text-[11px] text-[#EA7B2B] uppercase tracking-[0.25em] mt-3 pl-1">Beta Aste</div>
        </div>

        {token ? (
          // ---- Imposta nuova password ----
          done ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 className="w-10 h-10 text-[#10B981] mx-auto mb-3" />
              <h1 className="text-2xl font-light text-white mb-2">Password aggiornata</h1>
              <p className="text-sm text-zinc-500">Ti reindirizzo al login…</p>
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-light text-white mb-2">Nuova password</h1>
              <p className="text-sm text-zinc-500 mb-8">Scegli una nuova password per il tuo account.</p>
              <form onSubmit={submitReset} className="space-y-4" data-testid="reset-form">
                <div>
                  <label className="text-xs text-zinc-400 uppercase tracking-wider">Nuova password</label>
                  <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password-input" className={inputCls} required />
                </div>
                <div>
                  <label className="text-xs text-zinc-400 uppercase tracking-wider">Conferma password</label>
                  <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="reset-password-confirm" className={inputCls} required />
                </div>
                {error && <div className="text-sm text-[#E11D48] bg-[#E11D48]/10 border border-[#E11D48]/30 rounded-sm px-3 py-2" data-testid="reset-error">{error}</div>}
                <button type="submit" disabled={loading} data-testid="reset-submit" className="w-full bg-[#EA7B2B] text-black font-medium py-3 rounded-sm hover:bg-[#F59E4A] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} Reimposta password
                </button>
              </form>
            </>
          )
        ) : (
          // ---- Richiedi reset ----
          done ? (
            <div className="text-center" data-testid="forgot-success">
              <CheckCircle2 className="w-10 h-10 text-[#10B981] mx-auto mb-3" />
              <h1 className="text-2xl font-light text-white mb-2">Controlla la tua email</h1>
              <p className="text-sm text-zinc-500">Se l'indirizzo è registrato, riceverai un'email con il link per reimpostare la password (valido 60 minuti).</p>
              <Link to="/login" className="inline-flex items-center gap-2 text-sm text-[#EA7B2B] mt-6 hover:underline"><ArrowLeft className="w-4 h-4" /> Torna al login</Link>
            </div>
          ) : (
            <>
              <h1 className="text-3xl font-light text-white mb-2">Password dimenticata?</h1>
              <p className="text-sm text-zinc-500 mb-8">Inserisci la tua email: ti invieremo il link per reimpostarla.</p>
              <form onSubmit={requestReset} className="space-y-4" data-testid="forgot-form">
                <div>
                  <label className="text-xs text-zinc-400 uppercase tracking-wider">Email</label>
                  <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" className={inputCls} required />
                </div>
                {error && <div className="text-sm text-[#E11D48] bg-[#E11D48]/10 border border-[#E11D48]/30 rounded-sm px-3 py-2" data-testid="forgot-error">{error}</div>}
                <button type="submit" disabled={loading} data-testid="forgot-submit" className="w-full bg-[#EA7B2B] text-black font-medium py-3 rounded-sm hover:bg-[#F59E4A] transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
                  {loading && <Loader2 className="w-4 h-4 animate-spin" />} Invia link di reset
                </button>
              </form>
              <Link to="/login" className="inline-flex items-center gap-2 text-sm text-zinc-500 mt-6 hover:text-white transition-colors"><ArrowLeft className="w-4 h-4" /> Torna al login</Link>
            </>
          )
        )}
      </div>
    </div>
  );
}
